<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <h1 class="mb-2">Upgrades & Revenue Optimization</h1>
        <p class="grey--text text--darken-1">
          Review upgrade revenue, upgrade likelihood by package, and upgrade revenue compared to base package sales.
        </p>
      </v-col>
    </v-row>

    <!-- Filters -->
    <v-card class="mb-4">
      <v-card-title>
        Filters
        <v-spacer />
        <v-btn small color="primary" :loading="loading" @click="loadMpsRecords">
          Refresh
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.quarterKey"
              :items="quarterOptions"
              label="Booked Quarter"
              outlined
              dense
              clearable
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="filters.packageCode"
              :items="packageOptions"
              label="Package Code"
              outlined
              dense
              clearable
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="filters.rep"
              :items="repOptions"
              label="Salesperson"
              outlined
              dense
              clearable
            />
          </v-col>

          <v-col cols="12" md="3">
            <v-checkbox
              v-model="filters.bookedOnly"
              label="Booked records only"
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- KPI Cards -->
    <v-row class="mb-4">
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="caption grey--text">Booked Packages</div>
            <div class="headline font-weight-bold">{{ filteredRows.length }}</div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="caption grey--text">Packages With Upgrades</div>
            <div class="headline font-weight-bold">
              {{ upgradedPackageCount }} / {{ upgradeRate }}%
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="caption grey--text">Total Upgrade Revenue</div>
            <div class="headline font-weight-bold">
              {{ formatCurrency(totalUpgradeRevenue) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="caption grey--text">Avg Upgrade</div>
            <div class="headline font-weight-bold">
              {{ formatCurrency(averageUpgradeAmount) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mb-4">
      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="caption grey--text">Base Package Sales</div>
            <div class="headline font-weight-bold">
              {{ formatCurrency(totalBaseSales) }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="3">
        <v-card>
          <v-card-text>
            <div class="caption grey--text">Upgrade Revenue vs Base Sales</div>
            <div class="headline font-weight-bold">
              {{ upgradeRevenuePercent }}%
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Package Summary -->
    <v-card>
      <v-card-title>
        Upgrade Likelihood by Package
        <v-spacer />
        <v-text-field
          v-model="packageSearch"
          append-icon="mdi-magnify"
          label="Search Packages"
          single-line
          hide-details
          dense
          clearable
        />
      </v-card-title>

      <v-data-table
        :headers="packageHeaders"
        :items="packageSummaryRows"
        :search="packageSearch"
        :loading="loading"
        :items-per-page="25"
        sort-by="upgradeRate"
        sort-desc
        class="elevation-1"
      >
        <template v-slot:item.totalBaseSales="{ item }">
          {{ formatCurrency(item.totalBaseSales) }}
        </template>

        <template v-slot:item.totalUpgradeRevenue="{ item }">
          {{ formatCurrency(item.totalUpgradeRevenue) }}
        </template>

        <template v-slot:item.averageUpgradeAmount="{ item }">
          {{ formatCurrency(item.averageUpgradeAmount) }}
        </template>

        <template v-slot:item.upgradeRate="{ item }">
          <strong>{{ item.upgradeRate }}%</strong>
        </template>

        <template v-slot:item.upgradeRevenuePercent="{ item }">
          {{ item.upgradeRevenuePercent }}%
        </template>
      </v-data-table>
    </v-card>

    <!-- Detail -->
    <v-card class="mt-4">
      <v-card-title>
        Upgrade Detail
        <v-spacer />
        <v-text-field
          v-model="detailSearch"
          append-icon="mdi-magnify"
          label="Search Detail"
          single-line
          hide-details
          dense
          clearable
        />
      </v-card-title>

      <v-data-table
        :headers="detailHeaders"
        :items="filteredRows"
        :search="detailSearch"
        :loading="loading"
        :items-per-page="25"
        sort-by="upgrade"
        sort-desc
        dense
      >
        <template v-slot:item.bookedDate="{ item }">
          {{ formatDate(item.bookedDate || item.dateBooked) }}
        </template>

        <template v-slot:item.price="{ item }">
          {{ formatCurrency(item.price) }}
        </template>

        <template v-slot:item.upgrade="{ item }">
          <strong>{{ formatCurrency(item.upgrade) }}</strong>
        </template>

        <template v-slot:item.pl="{ item }">
          {{ formatCurrency(item.pl) }}
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import moment from "moment";
import { portalsApp } from "@/firebase";

export default {
  name: "UpgradesRevenue",

  data() {
    return {
      loading: false,
      packageSearch: "",
      detailSearch: "",

      filters: {
        quarterKey: null,
        packageCode: null,
        rep: null,
        bookedOnly: true,
      },

      rows: [],

      packageHeaders: [
        { text: "Package Code", value: "packageCode" },
        { text: "Destination", value: "packageDestination" },
        { text: "Booked Packages", value: "totalPackages" },
        { text: "Upgraded Packages", value: "upgradedPackages" },
        { text: "Upgrade Rate", value: "upgradeRate" },
        { text: "Base Sales", value: "totalBaseSales" },
        { text: "Upgrade Revenue", value: "totalUpgradeRevenue" },
        { text: "Avg Upgrade", value: "averageUpgradeAmount" },
        { text: "Upgrade % of Sales", value: "upgradeRevenuePercent" },
      ],

      detailHeaders: [
        { text: "Booked Date", value: "bookedDate" },
        { text: "Traveler", value: "traveler" },
        { text: "Package Code", value: "packageCode" },
        { text: "Destination", value: "packageDestination" },
        { text: "Salesperson", value: "rep" },
        { text: "Price", value: "price" },
        { text: "Upgrade", value: "upgrade" },
        { text: "P/L", value: "pl" },
        { text: "PUID", value: "puid" },
      ],
    };
  },

  computed: {
    quarterOptions() {
      var options = [];
      var currentYear = moment().year();
      var currentQuarter = moment().quarter();

      for (var year = 2019; year <= currentYear; year++) {
        var maxQuarter = year === currentYear ? currentQuarter : 4;

        for (var quarter = 1; quarter <= maxQuarter; quarter++) {
          options.push({
            text: year + " Q" + quarter,
            value: year + "-Q" + quarter,
          });
        }
      }

      return options.reverse();
    },

    filteredRows() {
      var output = this.rows.slice();

      if (this.filters.bookedOnly) {
        output = output.filter((row) => {
          return row.booked === true || row.bookedDate || row.dateBooked;
        });
      }

      if (this.filters.packageCode) {
        output = output.filter((row) => {
          return row.packageCode === this.filters.packageCode;
        });
      }

      if (this.filters.rep) {
        output = output.filter((row) => {
          return row.rep === this.filters.rep;
        });
      }

      if (this.filters.quarterKey) {
        var quarterParts = this.filters.quarterKey.split("-Q");
        var year = Number(quarterParts[0]);
        var quarter = Number(quarterParts[1]);

        var quarterStart = moment()
          .year(year)
          .quarter(quarter)
          .startOf("quarter");

        var quarterEnd = moment()
          .year(year)
          .quarter(quarter)
          .endOf("quarter");

        output = output.filter((row) => {
          var parsed = this.parseDate(row.dateBooked || row.bookedDate);
          if (!parsed || !parsed.isValid()) return false;
          return parsed.isBetween(quarterStart, quarterEnd, "day", "[]");
        });
      }

      return output;
    },

    packageOptions() {
      var values = [];

      this.rows.forEach((row) => {
        if (row.packageCode && values.indexOf(row.packageCode) === -1) {
          values.push(row.packageCode);
        }
      });

      return values.sort();
    },

    repOptions() {
      var values = [];

      this.rows.forEach((row) => {
        if (row.rep && values.indexOf(row.rep) === -1) {
          values.push(row.rep);
        }
      });

      return values.sort();
    },

    upgradedPackageCount() {
      var count = 0;

      this.filteredRows.forEach((row) => {
        if (this.toNumber(row.upgrade) > 0) count += 1;
      });

      return count;
    },

    upgradeRate() {
      if (!this.filteredRows.length) return "0.0";

      return ((this.upgradedPackageCount / this.filteredRows.length) * 100).toFixed(1);
    },

    totalUpgradeRevenue() {
      var total = 0;

      this.filteredRows.forEach((row) => {
        total += this.toNumber(row.upgrade);
      });

      return total;
    },

    totalBaseSales() {
      var total = 0;

      this.filteredRows.forEach((row) => {
        total += this.toNumber(row.price);
      });

      return total;
    },

    averageUpgradeAmount() {
      if (!this.upgradedPackageCount) return 0;

      return this.totalUpgradeRevenue / this.upgradedPackageCount;
    },

    upgradeRevenuePercent() {
      if (!this.totalBaseSales) return "0.0";

      return ((this.totalUpgradeRevenue / this.totalBaseSales) * 100).toFixed(1);
    },

    packageSummaryRows() {
      var map = {};

      this.filteredRows.forEach((row) => {
        var key = row.packageCode || "Unknown";

        if (!map[key]) {
          map[key] = {
            packageCode: key,
            packageDestination: row.packageDestination || "",
            totalPackages: 0,
            upgradedPackages: 0,
            totalBaseSales: 0,
            totalUpgradeRevenue: 0,
            averageUpgradeAmount: 0,
            upgradeRate: 0,
            upgradeRevenuePercent: 0,
          };
        }

        map[key].totalPackages += 1;
        map[key].totalBaseSales += this.toNumber(row.price);
        map[key].totalUpgradeRevenue += this.toNumber(row.upgrade);

        if (this.toNumber(row.upgrade) > 0) {
          map[key].upgradedPackages += 1;
        }
      });

      return Object.keys(map).map((key) => {
        var item = map[key];

        if (item.totalPackages > 0) {
          item.upgradeRate = Number(
            ((item.upgradedPackages / item.totalPackages) * 100).toFixed(1)
          );
        }

        if (item.upgradedPackages > 0) {
          item.averageUpgradeAmount =
            item.totalUpgradeRevenue / item.upgradedPackages;
        }

        if (item.totalBaseSales > 0) {
          item.upgradeRevenuePercent = Number(
            ((item.totalUpgradeRevenue / item.totalBaseSales) * 100).toFixed(1)
          );
        }

        return item;
      });
    },
  },

  mounted() {
    this.loadMpsRecords();
  },

  methods: {
    async loadMpsRecords() {
      this.loading = true;

      try {
        var snapshot = await portalsApp.firestore().collection("mps").get();
        var rows = [];

        snapshot.forEach((doc) => {
          var data = doc.data() || {};

          rows.push({
            id: doc.id,
            booked: data.booked || false,
            bookedDate: data.bookedDate || "",
            dateBooked: data.dateBooked || "",
            traveler: data.traveler || "",
            puid: data.puid || "",
            packageCode: String(data.packageCode || "").toUpperCase(),
            packageDestination: data.packageDestination || data.destination || "",
            rep: data.rep || "Unassigned",
            price: this.toNumber(data.price),
            upgrade: this.toNumber(data.upgrade),
            pl: this.toNumber(data.pl),
          });
        });

        this.rows = rows;
      } catch (error) {
        console.error("Error loading upgrades report:", error);

        if (this.$store) {
          this.$store.dispatch("setSnackbar", {
            display: true,
            color: "error",
            text: "Unable to load upgrades report",
            timeout: 5000,
          });
        }
      }

      this.loading = false;
    },

    parseDate(value) {
      if (!value) return null;

      var parsed = moment(
        value,
        [
          "YYYY-MM-DD",
          "MM-DD-YYYY",
          "M-D-YYYY",
          "MM/DD/YYYY",
          "M/D/YYYY",
          moment.ISO_8601,
        ],
        true
      );

      if (!parsed.isValid()) {
        parsed = moment(value);
      }

      return parsed;
    },

    toNumber(value) {
      if (value === undefined || value === null || value === "") return 0;

      if (typeof value === "string") {
        value = value.replace("$", "").replace(/,/g, "").trim();
      }

      var number = Number(value);

      if (isNaN(number)) return 0;

      return number;
    },

    formatCurrency(value) {
      return this.toNumber(value).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    },

    formatDate(value) {
      if (!value) return "";

      var parsed = this.parseDate(value);

      if (!parsed || !parsed.isValid()) return value;

      return parsed.format("MMM D, YYYY");
    },
  },
};
</script>